'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MarriageCalculator() {
  const t = useTranslations("tools.marriageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [spouse1, setSpouse1] = useState({
    salary: 65000,
    interest: 0,
    rental: 0,
    shortGain: 0,
    longGain: 0,
    qualified: 0,
    retirement: 10000,
    status: 'single',
    dependents: 0,
    useStandard: true,
    itemized: 0,
    stateTaxRate: 5,
    selfEmployed: false
  });

  const [spouse2, setSpouse2] = useState({
    salary: 45000,
    interest: 0,
    rental: 0,
    shortGain: 0,
    longGain: 0,
    qualified: 0,
    retirement: 6000,
    status: 'single',
    dependents: 0,
    useStandard: true,
    itemized: 0,
    stateTaxRate: 5,
    selfEmployed: false
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const taxBrackets2025 = {
    single: [
      { limit: 11600, rate: 0.10 },
      { limit: 47150, rate: 0.12 },
      { limit: 100525, rate: 0.22 },
      { limit: 191950, rate: 0.24 },
      { limit: 243725, rate: 0.32 },
      { limit: 609350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    married: [
      { limit: 23200, rate: 0.10 },
      { limit: 94300, rate: 0.12 },
      { limit: 201050, rate: 0.22 },
      { limit: 383900, rate: 0.24 },
      { limit: 487450, rate: 0.32 },
      { limit: 731200, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    hoh: [
      { limit: 16550, rate: 0.10 },
      { limit: 63100, rate: 0.12 },
      { limit: 100500, rate: 0.22 },
      { limit: 191950, rate: 0.24 },
      { limit: 243700, rate: 0.32 },
      { limit: 609350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ]
  };

  const standardDeductions = {
    single: 14600,
    married: 29200,
    hoh: 21900
  };

  const calculateTax = (income: number, brackets: any[]) => {
    let tax = 0;
    let previousLimit = 0;

    for (const bracket of brackets) {
      if (income > previousLimit) {
        const taxableInBracket = Math.min(income, bracket.limit) - previousLimit;
        tax += taxableInBracket * bracket.rate;
        previousLimit = bracket.limit;
      } else {
        break;
      }
    }

    return tax;
  };

  const calculateSpouseTax = (spouse: any) => {
    const grossIncome = spouse.salary + spouse.interest + spouse.rental + spouse.shortGain + spouse.longGain + spouse.qualified;
    const adjustedIncome = grossIncome - spouse.retirement;
    const deduction = spouse.useStandard ? standardDeductions[spouse.status as keyof typeof standardDeductions] : spouse.itemized;
    const taxableIncome = Math.max(0, adjustedIncome - deduction);

    let federalTax = calculateTax(taxableIncome, taxBrackets2025[spouse.status as keyof typeof taxBrackets2025]);

    if (spouse.selfEmployed) {
      federalTax += spouse.salary * 0.153;
    }

    const stateTax = adjustedIncome * (spouse.stateTaxRate / 100);
    const totalTax = federalTax + stateTax;

    return {
      grossIncome,
      adjustedIncome,
      deduction,
      taxableIncome,
      federalTax,
      stateTax,
      totalTax
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const spouse1Data = calculateSpouseTax(spouse1);
    const spouse2Data = calculateSpouseTax(spouse2);

    const combinedGross = spouse1Data.grossIncome + spouse2Data.grossIncome;
    const combinedRetirement = spouse1.retirement + spouse2.retirement;
    const combinedAdjusted = combinedGross - combinedRetirement;

    const marriedDeduction = Math.max(standardDeductions.married, spouse1.itemized + spouse2.itemized);
    const marriedTaxable = Math.max(0, combinedAdjusted - marriedDeduction);

    let marriedFederalTax = calculateTax(marriedTaxable, taxBrackets2025.married);

    if (spouse1.selfEmployed) marriedFederalTax += spouse1.salary * 0.153;
    if (spouse2.selfEmployed) marriedFederalTax += spouse2.salary * 0.153;

    const avgStateTax = (spouse1.stateTaxRate + spouse2.stateTaxRate) / 2;
    const marriedStateTax = combinedAdjusted * (avgStateTax / 100);
    const marriedTotalTax = marriedFederalTax + marriedStateTax;

    const separateTotalTax = spouse1Data.totalTax + spouse2Data.totalTax;
    const difference = separateTotalTax - marriedTotalTax;

    setResults({
      spouse1: spouse1Data,
      spouse2: spouse2Data,
      married: {
        grossIncome: combinedGross,
        adjustedIncome: combinedAdjusted,
        deduction: marriedDeduction,
        taxableIncome: marriedTaxable,
        federalTax: marriedFederalTax,
        stateTax: marriedStateTax,
        totalTax: marriedTotalTax
      },
      separateTotalTax,
      marriedTotalTax,
      difference
    });
  };

  const formatCurrency = (amount: number) => {
    return '$' + Math.round(amount).toLocaleString('en-US');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const spouseSectionStyle = getResponsiveStyle(
    { background: '#f8fafc', borderRadius: '0.75rem', marginBottom: '1.5rem' },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const spouseHeaderStyle = {
    fontWeight: 700,
    marginBottom: '1.25rem',
    paddingBottom: '0.75rem',
    borderBottom: '3px solid #e2e8f0',
    fontSize: screenSize === 'mobile' ? '1.1rem' : '1.2rem'
  };

  const spouse1HeaderStyle = { ...spouseHeaderStyle, color: '#2563eb', borderBottomColor: '#2563eb' };
  const spouse2HeaderStyle = { ...spouseHeaderStyle, color: '#7c3aed', borderBottomColor: '#7c3aed' };

  const formGroupStyle = { marginBottom: '1.25rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.65rem', fontSize: '0.95rem' },
    { padding: '0.7rem', fontSize: '0.975rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const radioGroupStyle = { display: 'flex', gap: '1.5rem', marginTop: '0.5rem' };

  const radioLabelStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', cursor: 'pointer' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 700,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.95rem 1.75rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #22c55e'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#166534', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const comparisonGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      textAlign: 'center' as const
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase' as const },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#1e293b' },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const highlightCardStyle = {
    ...resultCardStyle,
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)'
  };

  const savingsBannerStyle = getResponsiveStyle(
    {
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginTop: '1.5rem'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const savingsBannerH4Style = getResponsiveStyle(
    { margin: '0 0 0.5rem 0' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const savingsAmountStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '1.75rem' },
    { fontSize: '1.875rem' },
    { fontSize: '2rem' }
  );

  const detailTableStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.75rem', overflow: 'hidden', marginTop: '1.5rem' },
    { overflowX: 'auto' as const },
    {},
    {}
  );

  const tableStyle = { width: '100%', borderCollapse: 'collapse' as const };

  const thStyle = getResponsiveStyle(
    { background: '#f1f5f9', textAlign: 'left' as const, fontWeight: 700, color: '#334155', borderBottom: '2px solid #e2e8f0' },
    { padding: '0.75rem', fontSize: '0.85rem' },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #f1f5f9', color: '#475569' },
    { padding: '0.7rem', fontSize: '0.85rem' },
    { padding: '0.775rem', fontSize: '0.9rem' },
    { padding: '0.875rem', fontSize: '0.95rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { paddingLeft: '1.5rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = { marginBottom: '0.75rem', lineHeight: 1.6 };

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '1.75rem' },
    { marginBottom: '2rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.2rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>💍 {t("toolTitle")}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={spouseSectionStyle}>
                <div style={spouse1HeaderStyle}>👤 {t("spouse1Header")}</div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("salaryLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.salary}
                    onChange={(e) => setSpouse1({...spouse1, salary: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("interestLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.interest}
                    onChange={(e) => setSpouse1({...spouse1, interest: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("rentalLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.rental}
                    onChange={(e) => setSpouse1({...spouse1, rental: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("shortGainLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.shortGain}
                    onChange={(e) => setSpouse1({...spouse1, shortGain: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("longGainLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.longGain}
                    onChange={(e) => setSpouse1({...spouse1, longGain: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("qualifiedLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.qualified}
                    onChange={(e) => setSpouse1({...spouse1, qualified: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("retirementLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.retirement}
                    onChange={(e) => setSpouse1({...spouse1, retirement: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="500"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("statusLabel")}</label>
                  <select
                    style={inputStyle}
                    value={spouse1.status}
                    onChange={(e) => setSpouse1({...spouse1, status: e.target.value})}
                  >
                    <option value="single">{t("statusSingle")}</option>
                    <option value="hoh">{t("statusHoh")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("dependentsLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.dependents}
                    onChange={(e) => setSpouse1({...spouse1, dependents: parseInt(e.target.value) || 0})}
                    min="0"
                    max="10"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("deductionLabel")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={spouse1.useStandard}
                        onChange={() => setSpouse1({...spouse1, useStandard: true})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("deductionYes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={!spouse1.useStandard}
                        onChange={() => setSpouse1({...spouse1, useStandard: false})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("deductionNo")}
                    </label>
                  </div>
                </div>

                {!spouse1.useStandard && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("itemizedLabel")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={spouse1.itemized}
                      onChange={(e) => setSpouse1({...spouse1, itemized: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="100"
                    />
                  </div>
                )}

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("stateTaxLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse1.stateTaxRate}
                    onChange={(e) => setSpouse1({...spouse1, stateTaxRate: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="15"
                    step="0.1"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("selfEmployedLabel")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={spouse1.selfEmployed}
                        onChange={() => setSpouse1({...spouse1, selfEmployed: true})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("selfEmployedYes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={!spouse1.selfEmployed}
                        onChange={() => setSpouse1({...spouse1, selfEmployed: false})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("selfEmployedNo")}
                    </label>
                  </div>
                </div>
              </div>

              <div style={spouseSectionStyle}>
                <div style={spouse2HeaderStyle}>👤 {t("spouse2Header")}</div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("salaryLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.salary}
                    onChange={(e) => setSpouse2({...spouse2, salary: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("interestLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.interest}
                    onChange={(e) => setSpouse2({...spouse2, interest: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("rentalLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.rental}
                    onChange={(e) => setSpouse2({...spouse2, rental: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("shortGainLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.shortGain}
                    onChange={(e) => setSpouse2({...spouse2, shortGain: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("longGainLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.longGain}
                    onChange={(e) => setSpouse2({...spouse2, longGain: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("qualifiedLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.qualified}
                    onChange={(e) => setSpouse2({...spouse2, qualified: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("retirementLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.retirement}
                    onChange={(e) => setSpouse2({...spouse2, retirement: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="500"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("statusLabel")}</label>
                  <select
                    style={inputStyle}
                    value={spouse2.status}
                    onChange={(e) => setSpouse2({...spouse2, status: e.target.value})}
                  >
                    <option value="single">{t("statusSingle")}</option>
                    <option value="hoh">{t("statusHoh")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("dependentsLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.dependents}
                    onChange={(e) => setSpouse2({...spouse2, dependents: parseInt(e.target.value) || 0})}
                    min="0"
                    max="10"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("deductionLabel")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={spouse2.useStandard}
                        onChange={() => setSpouse2({...spouse2, useStandard: true})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("deductionYes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={!spouse2.useStandard}
                        onChange={() => setSpouse2({...spouse2, useStandard: false})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("deductionNo")}
                    </label>
                  </div>
                </div>

                {!spouse2.useStandard && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("itemizedLabel")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={spouse2.itemized}
                      onChange={(e) => setSpouse2({...spouse2, itemized: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="100"
                    />
                  </div>
                )}

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("stateTaxLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={spouse2.stateTaxRate}
                    onChange={(e) => setSpouse2({...spouse2, stateTaxRate: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="15"
                    step="0.1"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("selfEmployedLabel")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={spouse2.selfEmployed}
                        onChange={() => setSpouse2({...spouse2, selfEmployed: true})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("selfEmployedYes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        checked={!spouse2.selfEmployed}
                        onChange={() => setSpouse2({...spouse2, selfEmployed: false})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t("selfEmployedNo")}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              style={calculateBtnStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t("resultsTitle")}</h3>

              <div style={comparisonGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("separateLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.separateTotalTax)}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("jointLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.marriedTotalTax)}</span>
                </div>
                <div style={highlightCardStyle}>
                  <span style={{...resultLabelStyle, color: '#e0e7ff'}}>{t("differenceLabel")}</span>
                  <span style={{...resultValueStyle, color: 'white'}}>{formatCurrency(Math.abs(results.difference))}</span>
                </div>
              </div>

              <div style={{
                ...savingsBannerStyle,
                background: results.difference > 0
                  ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                  : results.difference < 0
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
              }}>
                <h4 style={savingsBannerH4Style}>
                  {results.difference > 0 ? '🎉 ' + t("bonusTitle") : results.difference < 0 ? '⚠️ ' + t("penaltyTitle") : t("noImpactTitle")}
                </h4>
                <div style={savingsAmountStyle}>{formatCurrency(Math.abs(results.difference))}</div>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: screenSize === 'mobile' ? '0.95rem' : '1rem' }}>
                  {results.difference > 0 ? t("bonusMessage") : results.difference < 0 ? t("penaltyMessage") : t("noImpactMessage")}
                </p>
              </div>

              <div style={detailTableStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("componentHeader")}</th>
                      <th style={thStyle}>{t("spouse1Header")}</th>
                      <th style={thStyle}>{t("spouse2Header")}</th>
                      <th style={thStyle}>{t("marriedJointHeader")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tdStyle}><strong>{t("grossIncomeRow")}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.spouse1.grossIncome)}</td>
                      <td style={tdStyle}>{formatCurrency(results.spouse2.grossIncome)}</td>
                      <td style={tdStyle}>{formatCurrency(results.married.grossIncome)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}><strong>{t("adjustedIncomeRow")}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.spouse1.adjustedIncome)}</td>
                      <td style={tdStyle}>{formatCurrency(results.spouse2.adjustedIncome)}</td>
                      <td style={tdStyle}>{formatCurrency(results.married.adjustedIncome)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}><strong>{t("deductionsRow")}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.spouse1.deduction)}</td>
                      <td style={tdStyle}>{formatCurrency(results.spouse2.deduction)}</td>
                      <td style={tdStyle}>{formatCurrency(results.married.deduction)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}><strong>{t("taxableIncomeRow")}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.spouse1.taxableIncome)}</td>
                      <td style={tdStyle}>{formatCurrency(results.spouse2.taxableIncome)}</td>
                      <td style={tdStyle}>{formatCurrency(results.married.taxableIncome)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}><strong>{t("federalTaxRow")}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.spouse1.federalTax)}</td>
                      <td style={tdStyle}>{formatCurrency(results.spouse2.federalTax)}</td>
                      <td style={tdStyle}>{formatCurrency(results.married.federalTax)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}><strong>{t("stateTaxRow")}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.spouse1.stateTax)}</td>
                      <td style={tdStyle}>{formatCurrency(results.spouse2.stateTax)}</td>
                      <td style={tdStyle}>{formatCurrency(results.married.stateTax)}</td>
                    </tr>
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={{...tdStyle, fontWeight: 700, borderBottom: 'none'}}><strong>{t("totalTaxRow")}</strong></td>
                      <td style={{...tdStyle, fontWeight: 700, borderBottom: 'none'}}>{formatCurrency(results.spouse1.totalTax)}</td>
                      <td style={{...tdStyle, fontWeight: 700, borderBottom: 'none'}}>{formatCurrency(results.spouse2.totalTax)}</td>
                      <td style={{...tdStyle, fontWeight: 700, borderBottom: 'none'}}>{formatCurrency(results.married.totalTax)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsPara1")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t("insightBenefit1Title")}</strong> {t("insightBenefit1Text")}</li>
          <li style={listItemStyle}><strong>{t("insightBenefit2Title")}</strong> {t("insightBenefit2Text")}</li>
          <li style={listItemStyle}><strong>{t("insightBenefit3Title")}</strong> {t("insightBenefit3Text")}</li>
          <li style={listItemStyle}><strong>{t("insightBenefit4Title")}</strong> {t("insightBenefit4Text")}</li>
        </ul>
        <p style={paragraphStyle}>{t("insightsPara2")}</p>
        <p style={paragraphStyle}>{t("insightsPara3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <p style={paragraphStyle}>{t("detailsPara1")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsSubtitle1Para")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailsSubtitle1Point1")}</li>
          <li style={listItemStyle}>{t("detailsSubtitle1Point2")}</li>
          <li style={listItemStyle}>{t("detailsSubtitle1Point3")}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsSubtitle2Para")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailsSubtitle2Point1")}</li>
          <li style={listItemStyle}>{t("detailsSubtitle2Point2")}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsSubtitle3Para")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/marriage-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://turbotax.intuit.com/tax-tips/marriage/7-tax-advantages-of-getting-married/L1XlLCh0m" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://smartasset.com/taxes/what-are-the-tax-benefits-of-marriage" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.plancorp.com/blog/4-financial-benefits-of-marriage" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref4")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}